import React from 'react'
import { Box, FormGroup, Grid, Tooltip } from '@material-ui/core'
import { IMPACT_CHOICES, CLIN_VAR_SEVERITY } from 'src/constants'
import { ACMG_CHOICES } from 'src/constants/acmg'
import ImpactChoiceChip from './ImpactChoiceChip'
import Label from 'src/components/Label'
import { useState, useEffect } from 'react'
import { add } from 'lodash'

const CLIN_VAR_TOOLTIPS = {
  exclude_any_benign: 'Filter-out variants which has Benign or Likely Benign associations in ClinVar.',
  pathogenic: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  likely_pathogenic: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  risk_factor: 'For variants that are interpreted not to cause a disorder but to increase the risk.',
  uncertain_significance: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  protective: 'For variants that decrease the risk of a disorder, including infections.',
  association: 'For variants identified in a GWAS study and further interpreted for their clinical significance.',
  drug_response: 'A general term for a variant that affects a drug response, not a disease.',
  conflicting_interpretations_of_pathogenicity:
    'Only for submissions from a consortium, where groups within the consortium have conflicting classifications of a variant but provide a single submission to ClinVar.',
  benign: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  likely_benign: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
}

const ACMG_TOOLTIPS = {
  'Uncertain significance': 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  Benign: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  'Likely benign': 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  Pathogenic: 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
  'Likely pathogenic': 'As recommended by ACMG/AMP for variants classified for Mendelian diseases.',
}

const IMPACT_TOOLTIPS = {
  transcript_ablation: 'A feature ablation whereby the deleted region includes a transcript feature',
  splice_acceptor_variant: "A splice variant that changes the 2 base region at the 3' end of an intron",
  splice_donor_variant: "A splice variant that changes the 2 base region at the 5' end of an intron",
  stop_gained:
    'A sequence variant whereby at least one base of a codon is changed, resulting in a premature stop codon, leading to a shortened transcript',
  frameshift_variant:
    'A sequence variant which causes a disruption of the translational reading frame, because the number of nucleotides inserted or deleted is not a multiple of three',
  stop_lost:
    'A sequence variant where at least one base of the terminator codon (stop) is changed, resulting in an elongated transcript',
  start_lost: 'A codon variant that changes at least one base of the canonical start codon',
  transcript_amplification: 'A feature amplification of a region containing a transcript',
  feature_elongation:
    'A sequence variant that causes the extension of a genomic feature, with regard to the reference sequence',
  feature_truncation:
    'A sequence variant that causes the reduction of a genomic feature, with regard to the reference sequence',
  inframe_insertion: 'An inframe non synonymous variant that inserts bases into the coding sequence',
  inframe_deletion: 'An inframe non synonymous variant that deletes bases from the coding sequence',
  missense_variant:
    'A sequence variant, that changes one or more bases, resulting in a different amino acid sequence but where the length is preserved',
  protein_altering_variant:
    'A sequence_variant which is predicted to change the protein encoded in the coding sequence',
  splice_donor_5th_base_variant:
    'A sequence variant that causes a change at the 5th base pair after the start of the intron in the orientation of the transcript',
  splice_region_variant:
    'A sequence variant in which a change has occurred within the region of the splice site, either within 1-3 bases of the exon or 3-8 bases of the intron',
  splice_donor_region_variant:
    "A sequence variant that falls in the region between the 3rd and 6th base after splice junction (5' end of intron)",
  splice_polypyrimidine_tract_variant:
    "A splice variant that falls in the polypyrimidine tract at 3' end of intron between 17 and 3 bases from the end (acceptor -3 to acceptor -17)",
  incomplete_terminal_codon_variant:
    'A sequence variant where at least one base of the final codon of an incompletely annotated transcript is changed',
  start_retained_variant:
    'A sequence variant where at least one base in the start codon is changed, but the start remains',
  stop_retained_variant:
    'A sequence variant where at least one base in the terminator codon is changed, but the terminator remains',
  synonymous_variant: 'A sequence variant where there is no resulting change to the encoded amino acid',
  coding_sequence_variant: 'A sequence variant that changes the coding sequence',
  mature_miRNA_variant: 'A transcript variant located with the sequence of the mature miRNA',
  '5_prime_UTR_variant': "A UTR variant of the 5' UTR",
  '3_prime_UTR_variant': "A UTR variant of the 3' UTR",
  non_coding_transcript_exon_variant:
    'A sequence variant that changes non-coding exon sequence in a non-coding transcript',
  intron_variant: 'A transcript variant occurring within an intron',
  NMD_transcript_variant: 'A variant in a transcript that is the target of NMD',
  non_coding_transcript_variant: 'A transcript variant of a non coding RNA gene',
  coding_transcript_variant: 'A transcript variant of a protein coding gene',
  upstream_gene_variant: "A sequence variant located 5' of a gene",
  downstream_gene_variant: "A sequence variant located 3' of a gene",
  TFBS_ablation: 'A feature ablation whereby the deleted region includes a transcription factor binding site',
  TFBS_amplification: 'A feature amplification of a region containing a transcription factor binding site',
  TF_binding_site_variant: 'A sequence variant located within a transcription factor binding site',
  regulatory_region_ablation: 'A feature ablation whereby the deleted region includes a regulatory region',
  regulatory_region_amplification: 'A feature amplification of a region containing a regulatory region',
  regulatory_region_variant: 'A sequence variant located within a regulatory region',
  intergenic_variant: 'A sequence variant located in the intergenic region, between genes',
  sequence_variant:
    'A sequence variant is a non exact copy of a sequence feature or genome exhibiting one or more sequence alteration',
}

function ImpactChoices(props) {
  const impactList = props.impactEvent
  const setImpactList = props.setImpactEvent

  const addToImpactList = (value) => {
    setImpactList((impactList) => [...impactList, value])
  }

  const removeFromImpactList = (value) => {
    setImpactList((list) => list.filter((item) => item !== value))
  }

  const handleImpactArray = (value) => {
    if (impactList.includes(value)) removeFromImpactList(value)
    else addToImpactList(value)
  }

  // This makes infinite re-renders
  // useEffect(() => {
  //   props.setImpactEvent(impactList)
  // }, [impactList, props])

  // Clicking a category header (HIGH / MODERATE / LOW / etc.) should
  // toggle the WHOLE GROUP, not per-chip. The old implementation called
  // ``handleImpactArray`` per value, which inverted each chip — so a
  // mixed selection (some on, some off) flipped to its inverse rather
  // than collapsing to all-on or all-off.
  //
  // New behaviour: if every chip in the group is already selected →
  // deselect them all (remove from list). Otherwise → add the missing
  // ones (no-op on already-selected chips). This matches the
  // "Exclude any benign" shortcut's set-semantics.
  const selectAll = (title) => {
    const clickedValues =
      props.filterType === 'impact'
        ? IMPACT_CHOICES[title].map((i) => i.value)
        : CLIN_VAR_SEVERITY[title].map((i) => i.value)
    setImpactList((list) => {
      const everySelected = clickedValues.every((v) => list.includes(v))
      if (everySelected) {
        const drop = new Set(clickedValues)
        return list.filter((v) => !drop.has(v))
      }
      const merged = new Set(list)
      clickedValues.forEach((v) => merged.add(v))
      return Array.from(merged)
    })
  }

  // Replace (not toggle) the impactList with the given set of values.
  // The "Exclude any benign" shortcut below SETs state to all
  // non-benign values — using ``forEach(handleImpactArray)`` (toggle)
  // would silently invert any currently-selected chips, so a second
  // click could leave only Benign selected, which is the opposite of
  // what the label says.
  const setSelection = (values) => {
    setImpactList(() => [...values])
  }

  const renderImpactChoiceGroup = ({ color, title, items, labelAction = () => selectAll(title) }) => {
    let tooltips = null
    if (props.filterType === 'clinvar') {
      tooltips = CLIN_VAR_TOOLTIPS
    } else if (props.filterType === 'acmg') {
      tooltips = ACMG_TOOLTIPS
    } else if (props.filterType === 'impact') {
      tooltips = IMPACT_TOOLTIPS
    }

    const isExcludeBenign = title === 'EXCLUDE ANY BENIGN'

    return (
      <>
        {props.filterType !== 'acmg' &&
          (isExcludeBenign ? (
            <Tooltip title={CLIN_VAR_TOOLTIPS?.exclude_any_benign || 'aa'}>
              <Box sx={{ display: 'inline-flex', width: '100%', padding: 0 }}>
                <Label
                  sx={{ cursor: 'pointer', width: '100%', display: 'inline-flex' }}
                  color={color}
                  onClick={labelAction}
                >
                  {title}
                </Label>
              </Box>
            </Tooltip>
          ) : (
            <Label sx={{ pointer: 'cursor' }} color={color} onClick={labelAction}>
              {title}
            </Label>
          ))}
        <FormGroup>
          <Box p={2}>
            {/* Predictable wrap: each chip gets a fixed proportion of
                row width based on the category's chip count, so a
                ``variant='outlined'→'filled'`` flip on click never
                bumps the layout. Categories with ≥4 chips wrap to
                multiple rows by design (e.g. ClinVar MODERATE has 5
                items — too many for one row at the 580 px drawer
                width). 1-3 items fit a single row; 4-6 items split
                into 2 rows; etc. */}
            <Grid container spacing={1}>
              {items.map(({ label, value }) => {
                const tooltipText = tooltips?.[value] || ''
                // Pick a column span (out of 12) that produces a
                // visually stable wrap. 1 item → full row, 2 → halves,
                // 3 → thirds, 4 → halves on 2 rows, 5-6 → thirds on 2
                // rows, 7+ → quarters.
                const n = items.length
                let colSpan = 4
                if (n === 1) colSpan = 12
                else if (n === 2) colSpan = 6
                else if (n === 3) colSpan = 4
                else if (n === 4) colSpan = 6
                else if (n <= 6) colSpan = 4
                else colSpan = 3
                return (
                  <Tooltip title={tooltipText ? tooltipText : ''}>
                    <Grid item xs={colSpan} key={value} sx={{ minWidth: 0 }}>
                      <ImpactChoiceChip
                        color={color}
                        key={value}
                        label={label}
                        onClick={() => handleImpactArray(value)}
                        isSelected={value}
                        impactList={impactList}
                      />
                    </Grid>
                  </Tooltip>
                )
              })}
            </Grid>
          </Box>
        </FormGroup>
      </>
    )
  }

  return (
    (props.filterType === 'impact' && (
      <Box display="flex" flexDirection="column">
        {renderImpactChoiceGroup({ color: 'error', title: 'HIGH', items: IMPACT_CHOICES.HIGH })}
        {renderImpactChoiceGroup({ color: 'warning', title: 'MODERATE', items: IMPACT_CHOICES.MODERATE })}
        {renderImpactChoiceGroup({ color: 'success', title: 'LOW', items: IMPACT_CHOICES.LOW })}
        {renderImpactChoiceGroup({ color: 'default', title: 'MODIFIER', items: IMPACT_CHOICES.MODIFIER })}
      </Box>
    )) ||
    (props.filterType === 'acmg' && (
      <Box display="flex" flexDirection="column">
        {renderImpactChoiceGroup({
          color: 'default',
          title: 'ACMG',
          items: ACMG_CHOICES.DESCRIPTIONS,
        })}
      </Box>
    )) ||
    (props.filterType === 'clinvar' && (
      <Box display="flex" flexDirection="column">
        {renderImpactChoiceGroup({
          color: 'purple',
          title: 'EXCLUDE ANY BENIGN',
          items: [],
          labelAction: () => {
            // Set the ClinVar filter to exactly the non-benign
            // categories (OTHER + HIGH + MODERATE). Plain replacement
            // — not a toggle — so clicking it always lands the user in
            // the same "anything but Benign / Likely benign" state
            // regardless of what was previously selected.
            const nonBenign = [
              ...CLIN_VAR_SEVERITY.OTHER.map((i) => i.value),
              ...CLIN_VAR_SEVERITY.HIGH.map((i) => i.value),
              ...CLIN_VAR_SEVERITY.MODERATE.map((i) => i.value),
            ]
            setSelection(nonBenign)
          },
        })}
        {renderImpactChoiceGroup({
          color: 'default',
          title: 'OTHER',
          items: CLIN_VAR_SEVERITY.OTHER,
        })}
        {renderImpactChoiceGroup({
          color: 'error',
          title: 'HIGH',
          items: CLIN_VAR_SEVERITY.HIGH,
        })}
        {renderImpactChoiceGroup({ color: 'warning', title: 'MODERATE', items: CLIN_VAR_SEVERITY.MODERATE })}
        {renderImpactChoiceGroup({
          color: 'success',
          title: 'LOW',
          items: CLIN_VAR_SEVERITY.LOW,
        })}
      </Box>
    ))
  )
}

export default ImpactChoices
